import axios from 'axios';

// Store recent API calls for debugging
export interface ApiCallLog {
  timestamp: number;
  method: string;
  url: string;
  params: any;
  data: any;
}

const MAX_HISTORY_SIZE = 30;
const API_CALL_HISTORY_KEY = 'rlms_api_call_history';

// Function to load history from localStorage
const loadApiCallHistory = (): ApiCallLog[] => {
  try {
    const storedHistory = localStorage.getItem(API_CALL_HISTORY_KEY);
    if (storedHistory) {
      return JSON.parse(storedHistory);
    }
  } catch (error) {
    console.error('Error loading API call history from localStorage:', error);
  }
  return [];
};

// Function to save history to localStorage
const saveApiCallHistory = (history: ApiCallLog[]) => {
  try {
    localStorage.setItem(API_CALL_HISTORY_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Error saving API call history to localStorage:', error);
  }
};

// Initialize from localStorage
const apiCallHistory: ApiCallLog[] = loadApiCallHistory();

// Utility to add call to history
const addToApiCallHistory = (call: ApiCallLog) => {
  // Add to beginning of history
  apiCallHistory.unshift(call);
  
  // Limit size
  if (apiCallHistory.length > MAX_HISTORY_SIZE) {
    apiCallHistory.pop();
  }
  
  // Save to localStorage
  saveApiCallHistory(apiCallHistory);
  
};

// Export function to get call history
export const getApiCallHistory = (): ApiCallLog[] => {
  // Reload from localStorage in case it was updated in another tab/component
  const history = loadApiCallHistory();
  return [...history];
};

// Export function to get the last API call
export const getLastApiCall = (): ApiCallLog | null => {
  const history = loadApiCallHistory();
  return history.length > 0 ? history[0] : null;
};

// Export function to clear history
export const clearApiCallHistory = (): void => {
  apiCallHistory.length = 0; // Clear the array
  saveApiCallHistory(apiCallHistory);
};

// Create axios instance with base configuration
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Create a function to get the current auth token and user email
export const getAuthHeader = () => {
  // Get token from localStorage
  const token = localStorage.getItem('auth_token') || localStorage.getItem('kc_token');
  
  // Try to extract email from token if available
  let email = null;
  if (token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const parsedToken = JSON.parse(jsonPayload);
      email = parsedToken.email;
    } catch (error) {
      console.error('Error parsing token in axios interceptor:', error);
    }
  }
  
  return {
    authHeader: token ? `Bearer ${token}` : '',
    email
  };
};

// Add a simple delay function to ensure token is available
const waitForToken = () => {
  return new Promise<void>((resolve) => {
    const checkToken = () => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('kc_token');
      if (token) {
        resolve();
      } else {
        setTimeout(checkToken, 100); // Check every 100ms
      }
    };
    checkToken();
  });
};

// Add request interceptor to include auth token and email in all requests
axiosInstance.interceptors.request.use(
  async (config) => {
    // Wait for token to be available (with timeout)
    try {
      await Promise.race([
        waitForToken(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Token timeout')), 3000))
      ]);
    } catch (error) {
      console.warn('Token not available within timeout, proceeding without auth header');
    }
    
    const { authHeader, email } = getAuthHeader();
    
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }
    
    if (email) {
      config.headers['X-User-Email'] = email;
    }
    
    // Log API call details to history
    const apiCall: ApiCallLog = {
      timestamp: Date.now(),
      method: config.method?.toUpperCase() || 'UNKNOWN',
      url: config.url || 'UNKNOWN_URL',
      params: config.params || {},
      data: config.data || {}
    };
    
    // Add to history
    addToApiCallHistory(apiCall);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Log error responses
    if (error.response) {
      console.error('%c API Error:', 'color: #cc0000; font-weight: bold;', {
        endpoint: error.response.config?.url,
        status: error.response.status,
        error: error.response.data,
        timestamp: new Date().toISOString()
      });
    } else {
      console.error('%c API Error:', 'color: #cc0000; font-weight: bold;', {
        error: error.message
      });
    }
    
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      // Redirect to login or refresh token
      console.error('Authentication error: Token may be expired');
      // You could trigger a token refresh here or redirect to login
    }
    
    return Promise.reject(error);
  }
);

// Add utility function to print last API call to console
export const logLastApiCall = () => {
  const lastCall = getLastApiCall();
  if (lastCall) {
    console.log('%c Last API Call:', 'background:#333; color:#fff; padding: 4px;', {
      endpoint: lastCall.url,
      method: lastCall.method,
      params: lastCall.params,
      data: lastCall.data,
      time: new Date(lastCall.timestamp).toLocaleTimeString()
    });
  } else {
    console.log('No API calls recorded yet.');
  }
};

export default axiosInstance; 